// Libs
import classNames from 'classnames/bind';
import {
  Heading,
  IconButton,
  IconCheckLine,
  IconEditLine,
  Spinner,
  Table,
  Text,
  TextInput,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BasePagination, BaseSelect, Loading } from '~/components/common';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { DEFAULT_LIMIT, DEFAULT_LIMIT_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { userActions } from '~/thunks/user/userSlice';
import { IStudent } from '~/utils/interface/profile';
import { getStudents, updateUsers } from '~/thunks/user/userThunk';
import { IQueryGetStudent } from '~/utils/interface/account';
import { IBaseSelectOption } from '~/utils/interface/common';
import { getCourses } from '~/thunks/course/courseThunk';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './Student.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const Student = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/student']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { students: studentsResponse, isLoading } = useAppSelector((state) => state.userState);
  //#endregion Selector

  //#region Declare State
  const [studentFocusId, setStudentFocusId] = useState<string>();
  const [students, setStudents] = useState<IStudent[]>([]);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [courseOptions, setCourseOptions] = useState<IBaseSelectOption[]>([]);
  const [courseSelected, setCourseSelected] = useState<string>();
  const [isLoadingOverlay, setIsLoadingOverlay] = useState<boolean>(false);
  const isAdminRole = localStorage.getItem(StorageEnum.ROLE) === RoleEnum.ADMIN;
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetStudents();
    if (isAdminRole) {
      handleGetCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setStudents(studentsResponse?.responses ?? []);
  }, [studentsResponse]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBack = () => {
    navigate(-1);
  };

  const handleGetStudents = async (query?: IQueryGetStudent) => {
    await dispatch(
      getStudents(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
        }
      )
    )
      .unwrap()
      .finally(() => {
        setIsLoadingOverlay(false);
      });
  };

  const handleGetCourses = async () => {
    await dispatch(
      getCourses({
        limit: DEFAULT_LIMIT,
      })
    )
      .unwrap()
      .then((res) => {
        setCourseOptions(
          res.data?.responses.map((item) => ({
            value: String(item.id),
            label: item.name,
          })) ?? []
        );
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleUpdateStudent = (studentId: string, identificationNumber: string) => {
    setStudentFocusId(undefined);
    dispatch(
      updateUsers({
        users: [
          {
            id: `${studentId}`,
            identificationNumber,
          },
        ],
      })
    )
      .unwrap()
      .then(() => {
        dispatch(userActions.setNewStudents(students));
      });
  };

  const handleClickEdit = (studentId: string) => {
    setStudents(studentsResponse?.responses ?? []);
    setStudentFocusId(studentId);
  };

  const handleChangeValue = (studentId: string, value: string) => {
    setStudents((prevState) =>
      prevState.map((item) => {
        if (studentId === item.id) {
          return {
            ...item,
            identificationNumber: value,
          };
        }
        return item;
      })
    );
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setIsLoadingOverlay(true);
    handleGetStudents({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const renderContent = () => {
    if (isLoading && (!studentsResponse || studentsResponse.responses.length === 0)) {
      return (
        <div className={cx('spinnerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (!studentsResponse || studentsResponse.responses.length === 0) {
      return (
        <div className={cx('spinnerContainer')}>
          <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
        </div>
      );
    }

    return (
      <>
        <View className={cx('studentListContainer')}>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='Name'>{t('student_name_label', { ns: 'admin/student' })}</Table.ColHeader>
                <Table.ColHeader id='Name'>{t('student_email_label', { ns: 'admin/student' })}</Table.ColHeader>
                <Table.ColHeader id='IdentificationNumber'>
                  {t('student_identification_number_label', { ns: 'admin/student' })}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {students.map((data, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <span>{data.fullName}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span>{data.email}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span>
                      {data.id === studentFocusId ? (
                        <TextInput
                          type={'tel'}
                          width={'120px'}
                          value={data.identificationNumber}
                          onChange={(e, newValue) => handleChangeValue(data.id, newValue)}
                          renderAfterInput={
                            <IconButton
                              withBorder={false}
                              withBackground={false}
                              color='primary'
                              onClick={() => handleUpdateStudent(data.id, data.identificationNumber ?? '')}
                              screenReaderLabel={'Save'}
                            >
                              {<IconCheckLine />}
                            </IconButton>
                          }
                        />
                      ) : (
                        <>
                          {data.identificationNumber ||
                            t('student_identification_number_not_set', { ns: 'admin/student' })}
                          {isAdminRole && (
                            <IconButton
                              withBorder={false}
                              withBackground={false}
                              color='primary'
                              onClick={() => handleClickEdit(data.id)}
                              screenReaderLabel={'Edit'}
                            >
                              {<IconEditLine />}
                            </IconButton>
                          )}
                        </>
                      )}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </View>

        <BasePagination currentPage={page} hasMore={studentsResponse?.hasMore} onChange={handleChangePage} />
      </>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('studentTitle')}>
          <div className={cx('studentHeaderTitle')}>
            <Heading level='h3'>{t('student_label', { ns: 'admin/student' })}</Heading>
            <img
              className={cx('iconHeader')}
              src={icons.commonIconStudent}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </div>

          {isAdminRole && (
            <BaseSelect
              value={courseSelected}
              placeholder={t('student_filter_by_course', { ns: 'admin/student' })}
              options={courseOptions}
              showDefaultOption={true}
              onChange={(option) => {
                setCourseSelected(option?.value);
                setPage(DEFAULT_PAGE);
                handleGetStudents({
                  courseId: option?.value,
                  limit: DEFAULT_LIMIT_PAGE,
                  page: DEFAULT_PAGE,
                });
              }}
            />
          )}
        </View>

        {renderContent()}
      </div>

      <Loading loading={isLoadingOverlay} overlay={true} />
    </div>
  );
};

export default Student;
